import { useAuth0 } from "@auth0/auth0-react";
import { useUpdateEffect } from "@react-hookz/web";
import { toZonedTime } from "date-fns-tz";
import type { TFunction } from "i18next";
import { useAtomValue } from "jotai";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { InformationIcon } from "~/components/ui/icons/information";
import { TranslateIcon } from "~/components/ui/icons/translate";
import type { OrderQuery } from "~/graphql/graphql-new.gen";
import {
  ClaimReason,
  OrderTagClaim,
  TrackingStatus,
} from "~/graphql/graphql-new.gen";
import { useTranslateClaimMutation } from "~/graphql/graphql.gen";
import { getI18nLanguageFullFromISOLanguage } from "~/libs/i18n";
import { brandState } from "~/stores/brand";
import { formatDate, relativeDate } from "~/utils/date";

import { ClaimActions } from "./claim-actions";
import { OrderAttachments } from "./order-attachments";
import { OrderClaimBadge } from "./order-claim-badge";
import { ProductErrorDetails } from "./product-error-details";
import { ProofDetails } from "./proof-details";

const getClaimReason = (
  reason: ClaimReason | null | undefined,
  t: TFunction,
) => {
  switch (reason) {
    case ClaimReason.DeliveredButDamaged:
      return t("claim.reasons.delivered_but_damaged");
    case ClaimReason.DeliveredButIncomplete:
      return t("claim.reasons.delivered_but_incomplete");
    case ClaimReason.DeliveredButWrong:
      return t("claim.reasons.delivered_but_wrong");
    case ClaimReason.DigitalCouponCodeNotWorking:
      return t("claim.reasons.digital_coupon_code_not_working");
    case ClaimReason.NotDeliveredAndReturnedToSender:
      return t("claim.reasons.not_delivered_and_returned_to_sender");
    case ClaimReason.NotDeliveredAndTrackingBlocked:
      return t("claim.reasons.not_delivered_and_tracking_blocked");
    case ClaimReason.NotDeliveredAndTrackingDelivered:
      return t("claim.reasons.not_delivered_and_tracking_delivered");
    case ClaimReason.Other:
      return t("claim.reasons.other");
    default:
      return "";
  }
};

const getClaimReasonOrOther = (
  reason: ClaimReason,
  otherReasonLabel: string | null | undefined,
  t: TFunction,
) => {
  if (reason === ClaimReason.Other && otherReasonLabel) {
    return otherReasonLabel;
  }
  return getClaimReason(reason, t);
};

const getClaimOrStatusMessage = (
  claim: OrderQuery["order"]["claims"][0],
  t: TFunction,
) => {
  const claimReason = getClaimReasonOrOther(
    claim.reason,
    claim.otherReasonLabel ?? "",
    t,
  );
  if (claimReason) {
    return claimReason;
  }

  return "";
};

type OrderClaimDetailsProps = {
  claim?: OrderQuery["order"]["claims"][0];
  order?: OrderQuery["order"];
  parcel?: OrderQuery["order"]["parcels"][0];
  tags: OrderQuery["order"]["tags"];
};

const MessageFromSupport = ({
  claim,
}: {
  claim: OrderQuery["order"]["claims"][0];
}) => {
  const { user } = useAuth0();
  const { t } = useTranslation();

  const [isOriginal, setIsOriginal] = useState(true);

  const { mutateAsync: translateClaimMutation, data: translateClaimData } =
    useTranslateClaimMutation();

  useUpdateEffect(() => {
    setIsOriginal(false);
  }, [translateClaimData]);

  const getOriginalLanguage = (lang: string) => {
    if (lang === "en") return t("claim.translate.english");
    return t("claim.translate.french");
  };

  const preferredLanguage =
    (user?.["https://appchoose.io/claims/preferredLanguage"] as
      | string
      | undefined) ?? navigator.language;

  return (
    <>
      {claim.messageFromSupport ? (
        <p className="whitespace-pre-line border-l-2 border-gray-100 pl-2 text-sm">
          {isOriginal || !translateClaimData
            ? claim.messageFromSupport.replaceAll("\n\n", "\n")
            : translateClaimData.translateClaim.translation}
        </p>
      ) : null}
      <div className="space-x-1">
        <TranslateIcon className="inline-block size-4" />
        {!isOriginal ? (
          <span className="text-xs text-gray-700">
            {t("claim.translate.original_language")}{" "}
            {getOriginalLanguage(
              translateClaimData?.translateClaim.originalLanguage ?? "fr",
            )}
          </span>
        ) : null}
        <button
          type="button"
          className="text-xs font-semibold text-green-900"
          onClick={() => {
            if (isOriginal && !translateClaimData)
              translateClaimMutation({
                claimId: claim.id,
                to: getI18nLanguageFullFromISOLanguage(preferredLanguage),
              });
            setIsOriginal(!isOriginal);
          }}
        >
          {isOriginal
            ? t("claim.translate.to_current_language")
            : t("claim.translate.original_text")}
        </button>
      </div>
    </>
  );
};

export const OrderClaimDetails: React.FC<OrderClaimDetailsProps> = ({
  claim,
  order,
  parcel,
  tags,
}: OrderClaimDetailsProps) => {
  const { i18n, t } = useTranslation();
  const [displayDetail, setDisplayDetail] = useState(false);

  const brand = useAtomValue(brandState);

  if (!order || !claim || tags.claim[0] === OrderTagClaim.None) return null;

  const claimReturn = order.returns.find((ret) => ret.claimId === claim.id);
  const showClaimReturnAlert =
    claimReturn?.tracking.status === TrackingStatus.Pending;

  if (!claim.isAccepted && !claim.isDeclined) {
    return (
      <div className="rounded border border-gray-300">
        <div className="flex items-center justify-between border-b border-gray-300 px-6 py-4">
          <OrderClaimBadge
            tag={tags.claim[0] ?? OrderTagClaim.None}
            fullLabel
          />
          <p className="text-xs text-[#767A7C]">
            {t("order.claim.opened_on", {
              date: formatDate(
                brand?.timezone
                  ? toZonedTime(new Date(claim.createdAt), brand.timezone)
                  : new Date(claim.createdAt),
                i18n.language.replace("_", "-"),
                {
                  day: "numeric",
                  month: "numeric",
                  year: "2-digit",
                },
              ),
              interpolation: { escapeValue: false },
            })}
          </p>
        </div>
        <div className="p-6 pt-4">
          <div>
            {showClaimReturnAlert ? (
              <Alert className="mb-4">
                <InformationIcon />
                <AlertTitle>
                  {t("order.return_request_sent_on", {
                    date: formatDate(
                      brand?.timezone
                        ? toZonedTime(
                            new Date(claimReturn.createdAt),
                            brand.timezone,
                          )
                        : new Date(claimReturn.createdAt),
                      i18n.language.replace("_", "-"),
                      {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                      },
                    ),
                    interpolation: { escapeValue: false },
                  })}
                </AlertTitle>
                <AlertDescription>
                  {t("order.return_can_choose_a_solution")}
                </AlertDescription>
              </Alert>
            ) : null}
            <div className="mb-2 text-sm text-gray-900">
              <div className="mb-4 font-semibold">
                {getClaimOrStatusMessage(claim, t)}
              </div>
              <MessageFromSupport claim={claim} />
            </div>
            <div className="space-x-1">
              <span className="text-xs text-gray-500">
                {relativeDate(
                  new Date(claim.createdAt),
                  i18n.language === "fr" ? "fr" : "en",
                  brand?.timezone ?? undefined,
                )}
              </span>
              {!order.isFullDigital && parcel?.trackingUrl ? (
                <>
                  <span className="text-xs text-gray-500">•</span>
                  <a
                    href={parcel.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-green-900"
                  >
                    {t("order.follow")}
                  </a>
                </>
              ) : null}
            </div>
            <ProductErrorDetails claim={claim} />
            <ProofDetails claim={claim} />
            <OrderAttachments claim={claim} />
          </div>

          <ClaimActions
            isFullDigital={order.isFullDigital}
            claim={claim}
            order={order}
            orderId={order.id}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded border border-gray-300">
      <div className="flex items-center justify-between border-b border-gray-300 px-6 py-4">
        <OrderClaimBadge tag={tags.claim[0] ?? OrderTagClaim.None} fullLabel />
        <p className="text-xs text-[#767A7C]">
          {t("order.claim.opened_on", {
            date: formatDate(
              brand?.timezone
                ? toZonedTime(new Date(claim.createdAt), brand.timezone)
                : new Date(claim.createdAt),
              i18n.language.replace("_", "-"),
              {
                day: "numeric",
                month: "numeric",
                year: "numeric",
              },
            ),
            interpolation: { escapeValue: false },
          })}
        </p>
      </div>
      <div className="p-6 pt-4">
        <div className="mb-2 text-sm text-gray-900">
          <div className="mb-4 font-semibold">
            {getClaimOrStatusMessage(claim, t)}
          </div>
        </div>

        {claim.acceptedMessage ? (
          <p className="whitespace-pre-line text-sm text-[#767A7C]">
            {`“${claim.acceptedMessage}“`}
          </p>
        ) : null}

        <ProductErrorDetails claim={claim} />

        <button
          type="button"
          className="mt-4 text-xs font-semibold text-green-900"
          onClick={() => setDisplayDetail(!displayDetail)}
        >
          {displayDetail
            ? t("order.claim.hide_detail")
            : t("order.claim.show_detail")}
        </button>
        {displayDetail ? (
          <div className="mt-4">
            <MessageFromSupport claim={claim} />

            <ProofDetails claim={claim} />

            <OrderAttachments claim={claim} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

OrderClaimDetails.displayName = "OrderClaimDetails";
